<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref, watch} from "vue";
import useSharedMethod from "@/use/SharedMethod.js";
import {useBoardingStore} from "@/stores/Boarding/Boarding.js";
import useSharedSelectData from "@/use/SharedSelectData";
import axios from "axios";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    components: {WCodeGeneration},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        category: String,
    },
    setup(props) {
        const {mxRules} = useValidation();
        const {
            loading,
            dialog,
            overlay,
        } = useSharedData();
        const {boardingCategories, boardingChronologies, boardingExecutions, boardingStatus} = useSharedSelectData();
        const {$route, $refs, $i18n} = useTools();
        const {getBack, getStatusColor} = useSharedMethod();
        
        const boardingStore = useBoardingStore();
        
        const readonly = computed(() => {
            return props.viewAction || props.deleteAction;
        });
        
        const boarding = ref({
            workerId: null,
            category: props.category,
            tasks: [],
        });
        
        const loaded = ref(false);
        const workerId = computed(() => boarding.value.workerId);
        const category = computed(() => boarding.value.category);
        
        watch([workerId, category], () => {
            if (workerId.value && category.value && props.addAction) {
                initialBoarding(workerId.value, category.value);
            }
        });
        
        function initialBoarding(workerId, category) {
            loading.value = true;
            axios.get(`/boardings/initialBoarding/${workerId}/${category}`).then(response => {
                boarding.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        const taskHeaders = [
            {
                text: "",
                align: "left",
                value: "action",
                sortable: false,
                width: "0%"
            },
            {
                text: $i18n.t("module.task_model"),
                align: "left",
                value: "name",
                sortable: true,
            },
            {
                text: $i18n.t("boarding.chronology"),
                align: "left",
                value: "chronology",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("boarding.recipient"),
                align: "left",
                value: "workerName",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("boarding.instruction"),
                align: "left",
                value: "instruction",
                sortable: true,
            },
            {
                text: $i18n.t("common.status"),
                align: "left",
                value: "status",
                sortable: true,
                width: "0%"
            },
        ];
        const actions = [
            {
                title: $i18n.t('common.take_in_charge'),
                icon: 'mdi-hand-pointing-right',
                color: 'blue darken-2',
                access: () => true,
                disabled: () => !props.viewAction,
                func: (item) => {
                    updateTask(item, 'TAKE_IN_CHARGE')
                },
            },
            {
                title: $i18n.t('common.terminate'),
                icon: 'mdi-hand-okay',
                color: 'teal',
                access: () => true,
                disabled: () => !props.viewAction,
                func: (item) => {
                    updateTask(item, 'TERMINATE')
                },
            },
            {
                title: $i18n.t('common.reassign'),
                icon: 'mdi-reload',
                color: 'purple',
                access: () => true,
                disabled: () => !props.viewAction,
                func: (item) => {
                    resign(item)
                },
            },
        ];
        
        function findById(id) {
            loading.value = true;
            axios.get(`/boardings/${id}`).then((response) => {
                boarding.value = response.data;
                loaded.value = true;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/boardings', boarding.value).then((response) => {
                    boardingStore.addBoarding(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/boardings', boarding.value).then((response) => {
                    boardingStore.updateBoarding(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function remove() {
            loading.value = true;
            axios.delete(`/boardings/${boarding.value.id}`).then(() => {
                boardingStore.deleteBoarding(boarding.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });
        
        const resignDialog = ref(false);
        const resignWorkerId = ref(null);
        const selectedTask = ref({});
        
        function resign(task) {
            selectedTask.value = task;
            resignDialog.value = true;
        }
        
        function updateTask(task, operation) {
            loading.value = true;
            axios.put(`/boardings/updateTask`, null, {
                params: {
                    taskId: task.id,
                    operation: operation,
                    workerId: resignWorkerId.value,
                }
            }).then(response => {
                boarding.value = response.data;
                boardingStore.updateBoarding(response.data);
                resignDialog.value = false;
                resignWorkerId.value = null;
                selectedTask.value = {};
            }).finally(() => {
                loading.value = false;
            })
        }
        
        
        return {
            resignDialog,
            resignWorkerId,
            selectedTask,
            taskHeaders,
            actions,
            boarding,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            boardingCategories,
            boardingChronologies,
            boardingExecutions,
            boardingStatus,
            getStatusColor,
            save,
            update,
            remove,
            updateTask,
            resign,
            getBack,
        };
    },
}
</script>

<template>
    <div>
        <v-dialog v-model="dialog" persistent scrollable max-width="1500">
            <v-card>
                <w-dialog-toolbar
                    :add-action="addAction"
                    :update-action="updateAction"
                    :delete-action="deleteAction"
                    :view-action="viewAction"
                ></w-dialog-toolbar>
                <w-overlay
                    :loading="loading"
                    :overlay="overlay"
                    :delete-action="deleteAction"
                    @remove="remove"
                    @cancel="overlay = false"
                ></w-overlay>
                <v-card-text>
                    <v-form ref="form" lazy-validation>
                        <v-container fluid>
                            <w-fieldset :title="`Processus ${boarding.processModelName ? boarding.processModelName : ''}`">
                                <v-row dense>
                                    <v-col cols="12" md="4">
                                        <w-label :label="$t('common.code')" required/>
                                        <w-code-generation
                                            v-model="boarding.code"
                                            :entity="'BOARDING'"
                                            :load="addAction"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        ></w-code-generation>
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <w-label :label="$t('module.worker')" required/>
                                        <str-worker-autocomplete
                                            v-model="boarding.workerId"
                                            action="SAVE_BOARDING"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="4">
                                        <w-label :label="$t('boarding.effective_date')" required/>
                                        <w-date-picker
                                            v-model="boarding.effectiveDate"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('boarding.advancement')">
                                <v-row dense>
                                    <v-col cols="4" class="mt-6">
                                        <strong>Status&nbsp;&nbsp;&nbsp;</strong>
                                        <v-chip
                                            v-if="boarding.status"
                                            :color="getStatusColor(`BOARDING_${boarding.status}`)"
                                            class="white--text"
                                        >
                                            <strong>
                                                {{ $t(`boarding.${boarding.status}`) }}
                                            </strong>
                                        </v-chip>
                                    </v-col>
                                    <v-col cols="4">
                                        <w-label :label="$t('boarding.planned_start_date')" required/>
                                        <w-date-picker
                                            v-model="boarding.plannedStartDate"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="4">
                                        <w-label :label="$t('boarding.planned_end_date')" required/>
                                        <w-date-picker
                                            v-model="boarding.plannedEndDate"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                </v-row>
                                <v-row dense>
                                    <v-col cols="4" class="mt-6">
                                        <div class="cnt">
                                                <span class="text">
                                                  <strong>{{ $t('boarding.advancement') }}</strong>
                                                </span>
                                            <v-progress-linear
                                                color="teal lighten-2"
                                                buffer-value="0"
                                                :value="(boarding.achievedTasks * 100 / boarding.tasksSize).toFixed(2)"
                                                stream
                                                height="25"
                                            >
                                                <strong>
                                                    {{ boarding.achievedTasks }}/{{ boarding.tasksSize }}
                                                    ({{ (boarding.achievedTasks * 100 / boarding.tasksSize).toFixed(2) }}%)
                                                </strong>
                                            </v-progress-linear>
                                        </div>
                                    
                                    </v-col>
                                    <v-col cols="4">
                                        <w-label :label="$t('boarding.actual_start_date')"/>
                                        <w-date-picker
                                            v-model="boarding.actualStartDate"
                                            readonly
                                        />
                                    </v-col>
                                    <v-col cols="4">
                                        <w-label :label="$t('boarding.actual_end_date')"/>
                                        <w-date-picker
                                            v-model="boarding.actualEndDate"
                                            readonly
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('module.task_models')">
                                <w-data-table
                                    :headers="taskHeaders"
                                    :items="boarding.tasks"
                                    :actions="actions"
                                    sort-by="order"
                                    disable-pagination
                                    fixed-header
                                    height="250"
                                >
                                    <template v-slot:item.name="{ item }">
                                        {{ item.name }}
                                    </template>
                                    <template v-slot:item.chronology="{ item }">
                                        <w-chip outlined :text="$t(`boarding.${item.chronology}`)"/>
                                    </template>
                                    <template v-slot:item.workerName="{ item }">
                                        <w-chip
                                            outlined
                                            :text="item.execution === 'ORGANIZATIONAL_CHART' && !item.workerId ? item.organizationalChartName : item.workerName"
                                        />
                                    </template>
                                    <template v-slot:item.status="{ item }">
                                        <w-chip :color="getStatusColor(`BOARDING_${item.status}`)" :text="$t(`boarding.${item.status}`)"/>
                                    </template>
                                </w-data-table>
                            </w-fieldset>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        :show="addAction"
                        :loading="loading"
                        @press="save"
                    />
                    <w-btn-update
                        :show="updateAction"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <v-dialog
            v-model="resignDialog"
            persistent
            max-width="600"
            scrollable
        >
            <v-card>
                <w-toolbar color="purple" :title="`Tâche: ${selectedTask.name}`"/>
                <v-card-text>
                    <v-form ref="resignForm" lazy-validation>
                        <v-container fluid>
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('module.worker')" required/>
                                    <str-worker-autocomplete
                                        v-model="resignWorkerId"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="resignDialog = false"/>
                    <w-btn
                        color="purple"
                        icon="mdi-reload"
                        text="Réassigner"
                        :loading="loading"
                        @press="updateTask(selectedTask, 'REASSIGN')"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<style scoped>
.cnt {
    display: flex;
    align-items: center;
}

.text {
    margin-right: 10px;
}

</style>